import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import { Dropdown, TextField, Stack, PrimaryButton, DefaultButton, Label } from '@fluentui/react';
import PropTypes from 'prop-types';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { dropdownStyles, textFieldStyles } from './styles';

export default function RSAAdCustomizerEditor(props) {
  const {
    options, updateAndCloseCallback, i18n, getAttributes,
  } = props;

  const [defaultText, setDefaultText] = useState(options.defaultText);
  const [showDropdownError, setShowDropdownError] = useState(false);
  const [adCustomizerAttibutes, setAdCustomizerAttibutes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(_.find(
    adCustomizerAttibutes,
    attribute => attribute.text === options.attributeName
  ));

  useEffect(() => {
    getAttributes().then((attributes) => {
      setAdCustomizerAttibutes(_.map(attributes, attribute => ({
        key: attribute.Id,
        text: attribute.Name,
        value: _.chain([attribute.AdGroupValue, attribute.CampaignValue, attribute.AccountValue])
          .compact()
          .value()
          .join(i18n.getString('RSAAdCustomizer_Attribute_Seperator')),
        datatype: attribute.Datatype,
      })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDefaultText(options.defaultText);
  }, [options.defaultText]);

  useEffect(() => {
    setSelectedAttribute(_.find(
      adCustomizerAttibutes,
      attribute => attribute.text === options.attributeName
    ));
  }, [adCustomizerAttibutes, options.attributeName]);

  const onSave = () => {
    if (!selectedAttribute) {
      setShowDropdownError(true);
      return;
    }

    const isDefaultTextEmpty =
      _.isUndefined(defaultText) ||
      _.isNull(defaultText) || _.isEmpty(defaultText);

    const newValue = `${options.prefix}{CUSTOMIZER.${selectedAttribute.text}${isDefaultTextEmpty ? '' : `:${defaultText}`}}${options.suffix}`;
    const cursorPosition = newValue.length - options.suffix.length;
    updateAndCloseCallback({
      newValue,
      cursorPosition,
      isSelectedByUser: true,
    });
  };

  const onCancel = () => {
    updateAndCloseCallback(({ isCancel: true }));
  };

  const onChange = (event, attribute) => {
    setSelectedAttribute(attribute);
  };

  const onRenderOption = option =>
    (
      <div className="attribute-item">
        <span className="attribute-name">{option.text} ({i18n.getString(`RSAAdCustomizer_Attribute_${option.datatype}`)})</span>
        <br />
        <span className="attribute-sample">{i18n.getString('RSAAdCustomizer_Samples')}: {option.value ? option.value : i18n.getString('RSAAdCustomizer_Attribute_None')}</span>
      </div>
    );

  const onRenderTitle = (selectedOptions) => {
    const option = selectedOptions[0];
    return (
      <div className="attribute-title">
        <span>{option.text} ({i18n.getString(`RSAAdCustomizer_Attribute_${option.datatype}`)})</span>
      </div>
    );
  };

  const onRenderLabel = () => (
    <Label>
      {i18n.getString('RSAAdCustomizer_Attribute')}
      <HelpPopupInline
        topic="pop_BA_AdCustomizerRSA_Attribute"
        ariaLabel={i18n.getString('Click to learn more about {{item}}', { item: i18n.getString('RSAAdCustomizer_Attribute') })}
      />
    </Label>
  );
  return (
    <div className="rsa-ad-customizer-editor">
      <form>
        <Dropdown
          placeholder={i18n.getString('RSAAdCustomizer_Select')}
          label={i18n.getString('RSAAdCustomizer_Attribute')}
          selectedKey={selectedAttribute ? selectedAttribute.key : null}
          errorMessage={showDropdownError ? i18n.getString('RSAAdCustomizer_Select_Error') : undefined}
          options={adCustomizerAttibutes}
          onChange={onChange}
          required
          styles={dropdownStyles}
          onRenderOption={onRenderOption}
          onRenderTitle={onRenderTitle}
          onRenderLabel={onRenderLabel}
        />
        <Label htmlFor="defaultTextInput">
          {i18n.getString('DefaultText_Label')}
          <HelpPopupInline
            topic="pop_BA_AdCustomizerRSA_DefaultText"
            ariaLabel={i18n.getString('Click to learn more about {{item}}', { item: i18n.getString('DefaultText_Label') })}
          />
        </Label>
        <TextField
          id="defaultTextInput"
          styles={textFieldStyles}
          className="rsa-ad-customizer-default-text-input"
          value={defaultText}
          onChange={(event, newValue) => setDefaultText(newValue)}
        />
        <Stack horizontal gap={12}>
          <PrimaryButton className="save" text={i18n.getString(('AdCustomizers_Editor_Set_Button'))} onClick={onSave} />
          <DefaultButton className="cancel" text={i18n.getString(('Cancel'))} onClick={onCancel} />
        </Stack>
      </form>
    </div>
  );
}

RSAAdCustomizerEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  options: PropTypes.shape({
    defaultText: PropTypes.string,
  }).isRequired,
  updateAndCloseCallback: PropTypes.func.isRequired,
  getAttributes: PropTypes.func.isRequired,
};
